import { Button } from "@/components/ui/button";
import { CreateFolderDialog } from "@/components/workspace/create-folder-dialog";
import { DeleteFolderDialog } from "@/components/workspace/delete-folder-dialog";
import { DeleteProjectDialog } from "@/components/workspace/delete-project-dialog";
import { RenameFolderDialog } from "@/components/workspace/rename-folder-dialog";
import { WorkspaceAppsPlaceholder } from "@/components/workspace/workspace-apps-placeholder";
import { WorkspaceBreadcrumbs } from "@/components/workspace/workspace-breadcrumbs";
import { WorkspaceGalleryView } from "@/components/workspace/workspace-gallery-view";
import { WorkspaceListView } from "@/components/workspace/workspace-list-view";
import { WorkspaceLoadingState } from "@/components/workspace/workspace-loading-state";
import { WorkspaceProjectPropertiesSheet } from "@/components/workspace/workspace-project-properties-sheet";
import { WorkspaceProjectToolbar } from "@/components/workspace/workspace-project-toolbar";
import { WorkspaceRefreshNotice } from "@/components/workspace/workspace-refresh-notice";
import { WorkspaceSidebar } from "@/components/workspace/workspace-sidebar";
import type { WorkspaceProject } from "@/components/workspace/workspace-types";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
import { useWorkspaceSearch } from "@/hooks/use-workspace-search";
import { useRegisterCommand } from "@/lib/command-registry";
import { Layers, RefreshCw } from "lucide-react";
/**
 * Workspace — the main project browser shell.
 *
 * Stage 2 wires the sidebar, breadcrumbs, gallery/list views, search, and
 * phase-2 dialogs. CRUD actions all surface "Coming in phase 2" toasts via
 * `useWorkspaceData`. The shell is fully responsive: sidebar collapses on
 * small screens, gallery stays single-column.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type View = "gallery" | "list";

/** Resolve the folder breadcrumb chain for a given folder id. */
function useFolderPath(folderId: string | null): string[] {
  const folders = useWorkspaceData((s) => s.folders);
  return useMemo(() => {
    if (!folderId) return [];
    const path: string[] = [];
    let current: string | null = folderId;
    while (current) {
      const folder = folders.find((f) => f.id === current);
      if (!folder) break;
      path.unshift(folder.id);
      current = folder.parent_id;
    }
    return path;
  }, [folders, folderId]);
}

export function Workspace(): JSX.Element {
  const folders = useWorkspaceData((s) => s.folders);
  const projects = useWorkspaceData((s) => s.projects);
  const loading = useWorkspaceData((s) => s.loading);
  const error = useWorkspaceData((s) => s.error);
  const lastFetchedAt = useWorkspaceData((s) => s.lastFetchedAt);
  const fetchWorkspace = useWorkspaceData((s) => s.fetch);

  const [view, setView] = useState<View>("gallery");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [renameTarget, setRenameTarget] = useState<string | null>(null);
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<string | null>(null);
  const [deleteProjectTarget, setDeleteProjectTarget] = useState<string | null>(null);
  const [propertiesProject, setPropertiesProject] = useState<WorkspaceProject | null>(null);

  const path = useFolderPath(selectedFolderId);

  // Auto-fetch on mount.
  useEffect(() => {
    if (!lastFetchedAt) void fetchWorkspace();
  }, [fetchWorkspace, lastFetchedAt]);

  // Register the top-level commands so the palette can find them.
  useRegisterCommand({
    id: "workspace.refresh",
    title: "Refresh workspace",
    group: "Workspace",
    shortcut: ["mod", "shift", "r"],
    keywords: ["reload", "fetch"],
    perform: () => {
      void fetchWorkspace();
    },
  });
  useRegisterCommand({
    id: "workspace.new-project",
    title: "New project…",
    group: "Workspace",
    keywords: ["create", "project"],
    perform: () => setShowCreateFolder(true),
  });
  useRegisterCommand({
    id: "workspace.toggle-view",
    title: view === "gallery" ? "Switch to list view" : "Switch to gallery view",
    group: "Workspace",
    perform: () => setView((v) => (v === "gallery" ? "list" : "gallery")),
  });

  const searchHits = useWorkspaceSearch(query);
  const filteredProjects = useMemo(() => {
    if (!query.trim()) return projects;
    const ids = new Set(
      searchHits.filter((h) => h.kind === "project").map((h) => h.project?.id ?? "")
    );
    return projects.filter((p) => ids.has(p.id));
  }, [projects, query, searchHits]);
  const filteredFolders = useMemo(() => {
    if (!query.trim()) return folders;
    const ids = new Set(
      searchHits.filter((h) => h.kind === "folder").map((h) => h.folder?.id ?? "")
    );
    return folders.filter((f) => ids.has(f.id));
  }, [folders, query, searchHits]);

  const renameTargetFolder = useMemo(
    () => (renameTarget ? folders.find((f) => f.id === renameTarget) : undefined),
    [folders, renameTarget]
  );
  const deleteFolderTargetFolder = useMemo(
    () => (deleteFolderTarget ? folders.find((f) => f.id === deleteFolderTarget) : undefined),
    [folders, deleteFolderTarget]
  );
  const deleteProjectTargetProject = useMemo(
    () => (deleteProjectTarget ? projects.find((p) => p.id === deleteProjectTarget) : undefined),
    [projects, deleteProjectTarget]
  );

  return (
    <div className="flex h-app-viewport flex-col bg-background text-foreground">
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-card px-4">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold">
            <Layers className="h-5 w-5 text-primary" />
            <span>puku-pcb</span>
          </Link>
          <span className="text-xs text-muted-foreground">Workspace</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono">mod</kbd>
            +<kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono">k</kbd>{" "}
            to open the command palette
          </span>
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden">
        <WorkspaceSidebar selectedFolderId={selectedFolderId} onSelect={setSelectedFolderId} />
        <main className="flex-1 overflow-auto">
          <div className="space-y-4 p-6">
            <WorkspaceAppsPlaceholder onOpenSettings={() => window.location.assign("/settings")} />
            <WorkspaceRefreshNotice />
            {error ? (
              <div className="flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                <span>Failed to load workspace: {error.message}</span>
                <Button variant="outline" size="sm" onClick={() => void fetchWorkspace()}>
                  <RefreshCw className="mr-1 h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : null}
            <WorkspaceBreadcrumbs path={path} onSelect={setSelectedFolderId} />
            <WorkspaceProjectToolbar
              view={view}
              onViewChange={setView}
              query={query}
              onQueryChange={setQuery}
              onCreateProject={() => setShowCreateFolder(true)}
              onCreateFolder={() => setShowCreateFolder(true)}
            />
            {loading && projects.length === 0 ? (
              <WorkspaceLoadingState />
            ) : view === "gallery" ? (
              <WorkspaceGalleryView
                projects={filteredProjects}
                folders={filteredFolders}
                onSelectFolder={(id) => setSelectedFolderId(id)}
                onCreateProject={() => setShowCreateFolder(true)}
                onCreateFolder={() => setShowCreateFolder(true)}
              />
            ) : (
              <WorkspaceListView
                projects={filteredProjects}
                folders={filteredFolders}
                onSelectFolder={(id) => setSelectedFolderId(id)}
              />
            )}
          </div>
        </main>
      </div>

      <CreateFolderDialog
        open={showCreateFolder}
        onOpenChange={setShowCreateFolder}
        parentId={selectedFolderId}
      />
      <RenameFolderDialog
        folderId={renameTarget}
        initialName={renameTargetFolder?.name}
        onOpenChange={(o) => !o && setRenameTarget(null)}
      />
      <DeleteFolderDialog
        folderId={deleteFolderTarget}
        folderName={deleteFolderTargetFolder?.name}
        projectCount={deleteFolderTargetFolder?.project_count}
        onOpenChange={(o) => !o && setDeleteFolderTarget(null)}
      />
      <DeleteProjectDialog
        projectId={deleteProjectTarget}
        projectName={deleteProjectTargetProject?.name}
        onOpenChange={(o) => !o && setDeleteProjectTarget(null)}
      />
      <WorkspaceProjectPropertiesSheet
        project={propertiesProject}
        onOpenChange={(o) => !o && setPropertiesProject(null)}
      />
    </div>
  );
}

export default Workspace;
